import * as React from "react";
import { Calendar as CalendarIcon, Trash2Icon } from "lucide-react";
import * as chrono from "chrono-node";

import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Matcher } from "react-day-picker";
import { useI18n } from "@/contexts/i18nContext";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "./input-group";
import { useLingui } from "@lingui/react/macro";
import { useState } from "react";
import { LOCALE } from "@/constants";

type DatePickerProps = {
  onChangeDate: (date: Date | undefined) => void;
  onInvalidDate?: (value: string) => void;
  disabled?: Matcher | Matcher[];
  clearLabel?: React.ReactNode;
  "aria-invalid"?: boolean;
  isClearable?: boolean;
  value?: Date;
  id?: string;
  min?: Date;
  max?: Date;
};

export default function DatePicker({
  value: currentDate,
  onChangeDate,
  onInvalidDate,
  isClearable,
  clearLabel,
  disabled,
  id,
  min,
  max,
  ...rest
}: DatePickerProps) {
  const { formatDate, language } = useI18n();
  const { t } = useLingui();

  // Local state
  const [open, setOpen] = useState<boolean>(false);
  const [month, setMonth] = useState<Date | undefined>(currentDate);
  const [textValue, setTextValue] = useState<string>(
    currentDate ? formatDate(currentDate, "PPP") : ""
  );

  // Helpers
  const disabledMatchers = React.useMemo<Matcher[]>(() => {
    const matchers: Matcher[] = [];
    if (disabled) {
      matchers.push(...(Array.isArray(disabled) ? disabled : [disabled]));
    }
    if (min) matchers.push({ before: min });
    if (max) matchers.push({ after: max });
    return matchers;
  }, [disabled, min, max]);

  function syncValue(newValue: string): boolean {
    const date =
      language === LOCALE.SWEDISH
        ? chrono.sv.parseDate(newValue)
        : chrono.parseDate(newValue);

    if (!date) {
      if (currentDate) {
        setTextValue(formatDate(currentDate, "PPP"));
      }
      return false;
    }

    onChangeDate(date);
    setMonth(date);

    return true;
  }

  // Side-effects
  // Keyed on the formatted string, not just `currentDate`: `formatDate` changes
  // identity when the locale/timezone resolves, and a stale string would be
  // re-parsed into a different date on blur.
  const formattedDate = currentDate ? formatDate(currentDate, "PPP") : "";
  const [prevFormattedDate, setPrevFormattedDate] = useState(formattedDate);
  if (formattedDate !== prevFormattedDate) {
    setPrevFormattedDate(formattedDate);
    setTextValue(formattedDate);
  }

  return (
    <InputGroup className="w-full">
      <InputGroupInput
        id={id}
        value={textValue}
        placeholder={t`Välj ett datum`}
        aria-invalid={rest["aria-invalid"]}
        onChange={(e) => {
          setTextValue(e.target.value);
        }}
        onBlur={(e) => {
          const typed = e.target.value;
          if (typed.trim() === "") return;
          const isValid = syncValue(typed);
          if (!isValid) {
            onInvalidDate?.(typed);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          } else if (e.key === "Enter") {
            syncValue(e.currentTarget.value);
            e.currentTarget.blur();
          }
        }}
      />

      <InputGroupAddon align="inline-end">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={<InputGroupButton size="icon-xs" type="button" />}
          >
            <CalendarIcon />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              month={month}
              captionLayout="dropdown"
              selected={currentDate}
              onMonthChange={setMonth}
              disabled={disabledMatchers}
              onSelect={(newDate) => {
                if (newDate) {
                  onChangeDate(newDate);
                  setTextValue(formatDate(newDate, "PPP"));
                }
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </InputGroupAddon>

      {isClearable && (
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            type="button"
            size="icon-xs"
            variant="ghost"
            onClick={() => {
              onChangeDate(undefined);
            }}
            aria-invalid={rest["aria-invalid"]}
          >
            <Trash2Icon />
            {clearLabel && <span className="sr-only">{clearLabel}</span>}
          </InputGroupButton>
        </InputGroupAddon>
      )}
    </InputGroup>
  );
}
